const VISITOR_KEY = "bidit_analytics_visitor_id";
const SESSION_KEY = "bidit_analytics_session";
const ATTRIBUTION_KEY = "bidit_analytics_attribution";
const INTERNAL_DEVICE_KEY = "bidit_analytics_internal_device";
const SESSION_TIMEOUT = 30 * 60 * 1000;

function hasAnalyticsConsent() {
  try {
    return Boolean(JSON.parse(localStorage.getItem("bidit.cookieConsent") || "null")?.ts);
  } catch (_) {
    return false;
  }
}

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = (Math.random() * 16) | 0;
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function getVisitorId() {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = uuid();
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

function getSession() {
  const now = Date.now();
  let session = null;
  try {
    session = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
  } catch (_) {}

  if (!session?.id || now - Number(session.lastActivity || 0) > SESSION_TIMEOUT) {
    session = { id: uuid(), startedAt: now, lastActivity: now };
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(readAttribution()));
  } else {
    session.lastActivity = now;
  }
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
}

function readAttribution() {
  const params = new URLSearchParams(window.location.search);
  let referrerSource = "direct";
  if (document.referrer) {
    try {
      const referrer = new URL(document.referrer);
      if (referrer.hostname !== window.location.hostname) referrerSource = referrer.hostname;
    } catch (_) {}
  }
  return {
    entry_page: `${window.location.pathname}${window.location.search}`.slice(0, 255),
    source: (params.get("utm_source") || referrerSource).slice(0, 100),
    medium: (params.get("utm_medium") || (referrerSource === "direct" ? "none" : "referral")).slice(0, 100),
    campaign: (params.get("utm_campaign") || "").slice(0, 150) || null,
    content: (params.get("utm_content") || "").slice(0, 150) || null,
  };
}

function getAttribution() {
  try {
    return JSON.parse(sessionStorage.getItem(ATTRIBUTION_KEY) || "null") || readAttribution();
  } catch (_) {
    return readAttribution();
  }
}

function getCookie(name) {
  const prefix = `${name}=`;
  const match = document.cookie.split(";").map((item) => item.trim()).find((item) => item.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : "";
}

function deviceType() {
  const width = window.innerWidth || document.documentElement.clientWidth;
  if (width < 768) return "mobile";
  if (width < 1100) return "tablet";
  return "desktop";
}

export function trackEvent(eventName, metadata = {}) {
  if (typeof window === "undefined") return Promise.resolve();
  if (!hasAnalyticsConsent()) {
    return new Promise((resolve) => {
      window.addEventListener("bidit-cookie-consent", () => {
        trackEvent(eventName, metadata).finally(resolve);
      }, { once: true });
    });
  }
  const session = getSession();
  const attribution = getAttribution();
  const payload = {
    visitor_id: getVisitorId(),
    session_id: session.id,
    event_name: eventName,
    page: `${window.location.pathname}${window.location.search}`.slice(0, 255),
    ...attribution,
    device_type: deviceType(),
    is_internal: localStorage.getItem(INTERNAL_DEVICE_KEY) === "true" || window.location.pathname.startsWith("/admin") || Boolean(getCookie("admintoken")),
    metadata,
  };
  const token = getCookie("token");
  const backend = process.env.React_APP_BACKEND_URL || "https://laravelapi.bidit.co.il";

  return fetch(`${backend}/api/analytics/event`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => undefined);
}

export function identifyAnalyticsUser() {
  return trackEvent("user_identified");
}

export function isAnalyticsInternalDevice() {
  return localStorage.getItem(INTERNAL_DEVICE_KEY) === "true";
}

export function setAnalyticsInternalDevice(internal) {
  const visitorId = getVisitorId();
  const adminToken = getCookie("admintoken");
  const backend = process.env.React_APP_BACKEND_URL || "https://laravelapi.bidit.co.il";
  return fetch(`${backend}/api/admin/analytics/internal-visitor`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
    },
    body: JSON.stringify({ visitor_id: visitorId, internal: Boolean(internal) }),
  }).then((response) => {
    if (!response.ok) throw new Error("internal visitor update failed");
    return response.json();
  }).then((data) => {
    if (internal) localStorage.setItem(INTERNAL_DEVICE_KEY, "true");
    else localStorage.removeItem(INTERNAL_DEVICE_KEY);
    return data;
  });
}

export function trackEventOnce(eventName, identity = "page", metadata = {}) {
  if (!hasAnalyticsConsent()) {
    return new Promise((resolve) => {
      window.addEventListener("bidit-cookie-consent", () => {
        trackEventOnce(eventName, identity, metadata).finally(resolve);
      }, { once: true });
    });
  }
  const session = getSession();
  const key = `bidit_analytics_once_${session.id}_${eventName}_${identity}`;
  if (sessionStorage.getItem(key)) return Promise.resolve();
  sessionStorage.setItem(key, "1");
  return trackEvent(eventName, metadata);
}
